// webapp/src/components/feedback/FeedbackProvider.tsx
/**
 * FeedbackProvider — React context that controls the feedback wizard.
 *
 * Single instance wraps the entire router (not per-route).
 * Provides openFeedback() and setSurveyContext() to children.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { FeedbackWizard } from './FeedbackWizard';
import type { FeedbackType } from './FeedbackWizard';
import type { SurveyContext } from '../../services/feedbackService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type { FeedbackType };

export interface FeedbackContextValue {
  openFeedback: (type: FeedbackType) => void;
  setSurveyContext: (ctx: SurveyContext | undefined) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const FeedbackContext = createContext<FeedbackContextValue | null>(null);

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/** Returns the feedback context. Must be used inside <FeedbackProvider>. */
// eslint-disable-next-line react-refresh/only-export-components
export function useFeedback(): FeedbackContextValue {
  const ctx = useContext(FeedbackContext);
  if (!ctx) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface FeedbackProviderProps {
  children: React.ReactNode;
}

export function FeedbackProvider({ children }: FeedbackProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('bug');
  const [surveyContext, setSurveyContextState] = useState<SurveyContext | undefined>(undefined);

  const openFeedback = useCallback((type: FeedbackType) => {
    console.log('[FeedbackProvider] openFeedback called with type:', type);
    setFeedbackType(type);
    setIsOpen(true);
  }, []);

  const closeFeedback = useCallback(() => {
    console.log('[FeedbackProvider] closeFeedback called');
    setIsOpen(false);
  }, []);

  const setSurveyContext = useCallback((ctx: SurveyContext | undefined) => {
    setSurveyContextState(ctx);
  }, []);

  const contextValue = useMemo(
    () => ({ openFeedback, setSurveyContext }),
    [openFeedback, setSurveyContext],
  );

  return (
    <FeedbackContext.Provider value={contextValue}>
      {children}
      {isOpen && (
        <FeedbackWizard
          type={feedbackType}
          onClose={closeFeedback}
          surveyContext={surveyContext}
        />
      )}
    </FeedbackContext.Provider>
  );
}
